import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Mail, Crown, ShieldCheck, Trash2, LogOut } from "lucide-react";

export default function Account() {
  const { user, logout, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success("Votre compte a été supprimé.");
      navigate("/");
    } catch {
      toast.error("La suppression a échoué.");
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 adminia-fade-in max-w-2xl mx-auto">
      <div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl">Mon compte</h1>
        <p className="text-muted-foreground mt-1">Gérez vos informations et votre abonnement.</p>
      </div>

      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-brand/10 flex items-center justify-center font-bold text-brand">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <p className="font-semibold">{user?.name}</p>
            <p className="text-sm text-muted-foreground inline-flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" /> {user?.email}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-surface p-4">
          <div className="flex items-center gap-2">
            {user?.is_premium ? <Crown className="h-5 w-5 text-success" /> : <ShieldCheck className="h-5 w-5 text-muted-foreground" />}
            <span className="font-medium">
              {user?.subscription_status === "trial" ? "Essai Premium" : user?.is_premium ? "Premium" : "Offre gratuite"}
            </span>
          </div>
          {!user?.is_premium && (
            <Button size="sm" className="rounded-full bg-brand hover:bg-brand/90" onClick={() => navigate("/app/abonnement")} data-testid="account-upgrade">
              Passer Premium
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="font-heading font-semibold text-lg mb-4">Actions</h2>
        <div className="space-y-3">
          <Button variant="outline" className="w-full rounded-full justify-start gap-2 h-12" onClick={logout} data-testid="account-logout">
            <LogOut className="h-4 w-4" /> Se déconnecter
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="w-full rounded-full justify-start gap-2 h-12 text-danger border-danger/30 hover:bg-danger/5" data-testid="account-delete-btn">
                <Trash2 className="h-4 w-4" /> Supprimer mon compte
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer votre compte ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Cette action est définitive. Tous vos courriers, documents analysés et justificatifs seront supprimés.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="account-delete-cancel">Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={doDelete} disabled={deleting}
                  className="bg-danger hover:bg-danger/90" data-testid="account-delete-confirm">
                  {deleting ? "Suppression…" : "Supprimer définitivement"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>
    </div>
  );
}
